import React, { useState, useMemo } from 'react';
import { DollarSign, Calendar, Clock, User, Phone, Filter, Search, CheckCircle, AlertCircle, History } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import PaymentModal from '../components/PaymentModal';
import PaymentHistoryModal from '../components/PaymentHistoryModal';
import { useBookings, usePayments, useSlotAvailability, useSlotConfiguration } from '../hooks/booking/useBookingContextSlices';
import { format } from 'date-fns';
import { composeSlotLabel } from '../utils/slotName';
import { Button, EmptyState, Input, KpiChip, ScorecardBookingCard, Select, StatusPill } from '../components/ui';

const PaymentsManagement = () => {
  const { bookings } = useBookings();
  const { markPaymentComplete } = usePayments();
  const { getGroundById, getServiceById } = useSlotAvailability();
  const { slotConfiguration } = useSlotConfiguration();
  
  // Helper function to format slot display with name
  const formatSlotDisplay = (booking) => {
    if (!booking.timeSlot && !booking.slotId) {
      if (booking.duration && (booking.serviceCategory === 'individual_sports' || booking.serviceType === 'individual_sports')) {
        return `Duration: ${booking.duration}`;
      }
      return 'N/A';
    }
    
    // Try to find slot in configuration
    let slotName = null;
    if (booking.groundId && booking.serviceId && slotConfiguration?.services) {
      const serviceConfig = slotConfiguration.services.find(config => 
        config.service?.id === parseInt(booking.serviceId) ||
        config.service?.name === booking.serviceName
      );
      if (serviceConfig?.service?.slots) {
        const slot = serviceConfig.service.slots.find(s => 
          s.id === booking.slotId || 
          `${s.startTime}-${s.endTime}` === booking.timeSlot
        );
        if (slot?.name) {
          slotName = slot.name;
        }
      }
    }
    
    // Compose a single, non-duplicated label (GA-004). Falls back to the slot
    // name discovered above, then to the booking's own slotName.
    return composeSlotLabel(slotName || booking.slotName, booking.timeSlot);
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending'); // 'pending', 'all', 'fully_paid'
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [selectedBookingForHistory, setSelectedBookingForHistory] = useState(null);

  // Helper function to calculate total amount correctly (baseAmount - discountAmount)
  const calculateTotalAmount = (booking) => {
    // If baseAmount and discountAmount are available, calculate from them
    if (booking.baseAmount !== undefined) {
      const base = parseFloat(booking.baseAmount || 0);
      const discount = parseFloat(booking.discountAmount || 0);
      return Math.max(0, base - discount);
    }
    // Otherwise use the provided totalAmount
    return parseFloat(booking.totalAmount || booking.price || 0);
  };

  // Filter bookings with pending payments - includes ALL booking types (team sports, individual sports, etc.)
  const pendingPaymentsBookings = useMemo(() => {
    return bookings.filter(booking => {
      // Skip cancelled bookings
      if (booking.status === 'cancelled') {
        return false;
      }

      // Calculate payment status for all booking types (team sports, individual sports, practice nets, open nets)
      const totalAmount = calculateTotalAmount(booking);
      const advanceAmount = parseFloat(booking.advanceAmount || 0);
      // Balance is always calculated as: Total Amount - Advance Amount
      const balanceAmount = Math.max(0, totalAmount - advanceAmount);
      
      // Determine payment status - works for all booking types including individual_sports
      // Individual sports bookings might not have paymentStatus set, so we calculate it
      const hasPendingPayment = booking.paymentStatus === 'pending' || 
                                balanceAmount > 0 ||
                                (totalAmount > 0 && advanceAmount < totalAmount);
      
      const isFullyPaid = booking.paymentStatus === 'fully_paid' || 
                          (balanceAmount === 0 && totalAmount > 0) ||
                          (totalAmount > 0 && advanceAmount >= totalAmount);
      
      // Filter by status filter
      let matchesStatus = true;
      if (statusFilter === 'pending') {
        matchesStatus = hasPendingPayment;
      } else if (statusFilter === 'fully_paid') {
        matchesStatus = isFullyPaid;
      }
      // 'all' shows everything

      // Filter by search term - includes service name and booking type for better search
      const matchesSearch = !searchTerm || 
        booking.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.contactPhone?.includes(searchTerm) ||
        booking.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.serviceName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.serviceType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.serviceCategory?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.id?.toString().includes(searchTerm);

      return matchesStatus && matchesSearch;
    }).sort((a, b) => {
      // Sort by date (upcoming first)
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });
  }, [bookings, statusFilter, searchTerm]);

  // Calculate summary statistics - includes all booking types
  const summaryStats = useMemo(() => {
    const pending = bookings.filter(b => {
      if (b.status === 'cancelled') return false;
      
      const totalAmount = calculateTotalAmount(b);
      const advanceAmount = parseFloat(b.advanceAmount || 0);
      // Balance is always calculated as: Total Amount - Advance Amount
      const balanceAmount = Math.max(0, totalAmount - advanceAmount);
      
      return b.paymentStatus === 'pending' || 
             balanceAmount > 0 ||
             (totalAmount > 0 && advanceAmount < totalAmount);
    });
    
    const totalPendingAmount = pending.reduce((sum, b) => {
      const totalAmount = calculateTotalAmount(b);
      const advanceAmount = parseFloat(b.advanceAmount || 0);
      // Balance is always calculated as: Total Amount - Advance Amount
      const balanceAmount = Math.max(0, totalAmount - advanceAmount);
      return sum + balanceAmount;
    }, 0);
    
    const totalPaidAmount = bookings.reduce((sum, b) => sum + parseFloat(b.advanceAmount || 0), 0);
    const totalRevenue = bookings.reduce((sum, b) => sum + calculateTotalAmount(b), 0);

    return {
      pendingCount: pending.length,
      totalPendingAmount,
      totalPaidAmount,
      totalRevenue
    };
  }, [bookings]);

  const handleOpenPaymentModal = (booking) => {
    setSelectedBooking(booking);
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedBooking(null);
  };

  const handleRecordPayment = (paymentInfo) => {
    if (selectedBooking) {
      markPaymentComplete(selectedBooking.id, paymentInfo);
      handleClosePaymentModal();
    }
  };

  const handleOpenPaymentHistory = (booking) => {
    setSelectedBookingForHistory(booking);
    setShowPaymentHistoryModal(true);
  };

  const handleClosePaymentHistory = () => {
    setShowPaymentHistoryModal(false);
    setSelectedBookingForHistory(null);
  };

  const getServiceName = (booking) => {
    // For individual sports, use serviceName directly
    if (booking.serviceCategory === 'individual_sports' || booking.serviceType === 'individual_sports') {
      return booking.serviceName || 'Individual Sports Service';
    }
    
    // For team sports and other types, try to get from ground/service
    if (booking.groundId && booking.serviceId) {
      const ground = getGroundById(booking.groundId);
      const service = getServiceById(booking.groundId, booking.serviceId);
      return service?.name || ground?.name || booking.serviceName || 'Unknown Service';
    }
    return booking.serviceName || 'Unknown Service';
  };

  const getServiceTypeLabel = (booking) => {
    if (booking.serviceType === 'cricket_match' || booking.serviceType === 'team_sports') return 'Team Sports';
    if (booking.serviceType === 'individual_sports' || booking.serviceCategory === 'individual_sports') return 'Individual Sports';
    if (booking.serviceType === 'practice_net') return 'Practice Net';
    if (booking.serviceType === 'open_net') return 'Open Net';
    return booking.serviceType || booking.serviceCategory || null;
  };

  // Derived per-booking payment figures used by both the card and table views.
  const getPaymentFigures = (booking) => {
    const totalAmount = calculateTotalAmount(booking);
    const advanceAmount = parseFloat(booking.advanceAmount || 0);
    const balanceAmount = Math.max(0, totalAmount - advanceAmount);
    const isPending = booking.paymentStatus === 'pending' ||
                      balanceAmount > 0 ||
                      (totalAmount > 0 && advanceAmount < totalAmount);
    return { totalAmount, advanceAmount, balanceAmount, isPending };
  };

  const emptyState = (
    <EmptyState
      card
      icon={DollarSign}
      title="No bookings found"
      description={statusFilter === 'pending'
        ? 'No pending payments at the moment'
        : 'No bookings match your filters'}
    />
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-ga-text-primary mb-2 flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-ga-primary" />
            Payments Management
          </h1>
          <p className="text-ga-text-muted">
            Manage pending payments and track payment history for all bookings
          </p>
        </div>

        {/* KPI chips */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4">
          <KpiChip
            label="Pending Payments"
            value={summaryStats.pendingCount}
            icon={AlertCircle}
            accent="warning"
          />
          <KpiChip
            label="Pending Amount"
            value={`₹${summaryStats.totalPendingAmount.toFixed(2)}`}
            icon={DollarSign}
            accent="warning"
          />
          <KpiChip
            label="Total Paid"
            value={`₹${summaryStats.totalPaidAmount.toFixed(2)}`}
            icon={CheckCircle}
            accent="success"
          />
          <KpiChip
            label="Total Revenue"
            value={`₹${summaryStats.totalRevenue.toFixed(2)}`}
            icon={DollarSign}
            accent="primary"
          />
        </div>

        {/* Filters */}
        <div className="ga-card !p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-ga-text-subtle" />
                <Input
                  type="text"
                  placeholder="Search by name, phone, email, or booking ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-ga-text-muted" />
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="md:w-auto"
              >
                <option value="pending">Pending Payments</option>
                <option value="fully_paid">Fully Paid</option>
                <option value="all">All Bookings</option>
              </Select>
            </div>
          </div>
        </div>

        {pendingPaymentsBookings.length === 0 ? (
          emptyState
        ) : (
          <>
            {/* Payment scorecards — GA-010: on small/medium screens the wide
                table is replaced by cards whose Record Payment action is
                always visible, never clipped by horizontal overflow. */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:hidden">
              {pendingPaymentsBookings.map((booking) => {
                const { totalAmount, advanceAmount, balanceAmount, isPending } = getPaymentFigures(booking);
                return (
                  <ScorecardBookingCard
                    key={booking.id}
                    title={booking.contactName || 'N/A'}
                    service={getServiceName(booking)}
                    ground={getServiceTypeLabel(booking)}
                    time={`${booking.date ? format(new Date(booking.date), 'MMM dd, yyyy') : 'N/A'} · ${formatSlotDisplay(booking)}`}
                    amount={`₹${totalAmount.toFixed(2)}`}
                    paymentStatus={isPending ? 'pending' : 'paid'}
                    actions={(
                      <>
                        {isPending && (
                          <Button
                            onClick={() => handleOpenPaymentModal(booking)}
                            className="!px-3 !py-1.5 !text-xs"
                          >
                            <DollarSign className="h-3.5 w-3.5" /> Record payment
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          onClick={() => handleOpenPaymentHistory(booking)}
                          className="!px-3 !py-1.5 !text-xs"
                        >
                          <History className="h-3.5 w-3.5" /> History
                        </Button>
                      </>
                    )}
                  >
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ga-text-muted">
                      <span>#{booking.id}</span>
                      {booking.contactPhone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3 w-3" aria-hidden />
                          {booking.contactPhone}
                        </span>
                      )}
                      <span>
                        Paid <span className="font-medium text-ga-success">₹{advanceAmount.toFixed(2)}</span>
                        {' · '}
                        Balance <span className={`font-medium ${balanceAmount > 0 ? 'text-ga-warning' : 'text-ga-success'}`}>₹{balanceAmount.toFixed(2)}</span>
                      </span>
                    </div>
                  </ScorecardBookingCard>
                );
              })}
            </div>

            {/* Wide-screen table — GA-010: consolidated columns plus a sticky
                Action column so Record Payment can never be pushed off-screen. */}
            <div className="ga-card hidden !p-0 overflow-hidden xl:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-ga-border-subtle">
                  <thead className="bg-ga-bg-page">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-subtle uppercase tracking-wider">
                        Booking ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-subtle uppercase tracking-wider">
                        Service
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-subtle uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-subtle uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-subtle uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-subtle uppercase tracking-wider">
                        Status
                      </th>
                      <th className="sticky right-0 bg-ga-bg-page px-6 py-3 text-left text-xs font-medium text-ga-text-subtle uppercase tracking-wider shadow-[inset_1px_0_0_0] shadow-ga-border-subtle">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ga-border-subtle">
                    {pendingPaymentsBookings.map((booking) => {
                      const { totalAmount, advanceAmount, balanceAmount, isPending } = getPaymentFigures(booking);
                      return (
                        <tr key={booking.id} className="group hover:bg-ga-bg-surface-subtle">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-ga-text-primary">
                              #{booking.id}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-ga-text-primary">{getServiceName(booking)}</div>
                            {getServiceTypeLabel(booking) && (
                              <div className="text-xs text-ga-text-subtle mt-1">
                                {getServiceTypeLabel(booking)}
                                {booking.duration && (booking.serviceCategory === 'individual_sports' || booking.serviceType === 'individual_sports') && (
                                  <span className="ml-1">({booking.duration})</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-ga-text-subtle" />
                              <div className="text-sm">
                                <div className="text-ga-text-primary">
                                  {booking.date ? format(new Date(booking.date), 'MMM dd, yyyy') : 'N/A'}
                                </div>
                                <div className="text-ga-text-subtle flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatSlotDisplay(booking)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="font-medium text-ga-text-primary flex items-center gap-1">
                                <User className="h-4 w-4 text-ga-text-subtle" />
                                {booking.contactName || 'N/A'}
                              </div>
                              <div className="text-ga-text-subtle flex items-center gap-1 mt-1">
                                <Phone className="h-3 w-3" />
                                {booking.contactPhone || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-ga-text-primary">
                              ₹{totalAmount.toFixed(2)}
                            </div>
                            <div className="text-xs mt-1">
                              <span className="text-ga-success">Paid ₹{advanceAmount.toFixed(2)}</span>
                              <span className="text-ga-text-subtle"> · </span>
                              <span className={balanceAmount > 0 ? 'text-ga-warning font-semibold' : 'text-ga-success'}>
                                Due ₹{balanceAmount.toFixed(2)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <StatusPill status={isPending ? 'pending' : 'paid'} label={isPending ? 'Pending' : 'Fully Paid'} size="sm" />
                          </td>
                          <td className="sticky right-0 bg-ga-bg-surface px-6 py-4 whitespace-nowrap text-sm font-medium shadow-[inset_1px_0_0_0] shadow-ga-border-subtle group-hover:bg-ga-bg-surface-subtle">
                            <div className="flex items-center gap-2">
                              {isPending && (
                                <Button
                                  onClick={() => handleOpenPaymentModal(booking)}
                                  className="!px-3 !py-1.5 !text-xs"
                                  title="Record Payment"
                                >
                                  <DollarSign className="h-3.5 w-3.5" />
                                  Record Payment
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                onClick={() => handleOpenPaymentHistory(booking)}
                                className="!px-3 !py-1.5 !text-xs"
                                title="View Payment History"
                              >
                                <History className="h-3.5 w-3.5" />
                                History
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedBooking && (
        <PaymentModal
          booking={selectedBooking}
          onClose={handleClosePaymentModal}
          onConfirm={handleRecordPayment}
        />
      )}

      {/* Payment History Modal */}
      {showPaymentHistoryModal && selectedBookingForHistory && (
        <PaymentHistoryModal
          booking={selectedBookingForHistory}
          onClose={handleClosePaymentHistory}
        />
      )}
    </DashboardLayout>
  );
};

export default PaymentsManagement;
